// frontend/src/admin/blockforms/WorksheetForm.js
import React, { useState, useEffect, useRef } from 'react';
import { WorksheetBlock } from '../../pages/ContentBlockRenderer';
import { cleanExcelHtml } from '../../utils/excelWorksheet';
import { buildBlockSave } from './blockSaveTarget';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://80.225.197.31:8001';

const WorksheetForm = ({ level, entityId, section, editingBlock, nextOrderIndex, onSaved, onCancel }) => {
    const existing = editingBlock?.content_data || {};

    const [title, setTitle]       = useState(existing.en?.title || '');
    const [htmlEn, setHtmlEn]     = useState(existing.en?.content || '');
    const [htmlHi, setHtmlHi]     = useState(existing.hi?.content || '');
    const [addHindi, setAddHindi] = useState(!!existing.hi?.content);
    const [saving, setSaving]     = useState(false);
    const [error, setError]       = useState(null);

    const enRef = useRef(null);
    const hiRef = useRef(null);

    useEffect(() => {
        const beforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', beforeUnload);
        return () => window.removeEventListener('beforeunload', beforeUnload);
    }, []);

    const handlePaste = (setter) => (e) => {
        e.preventDefault();
        const html  = e.clipboardData.getData('text/html');
        const plain = e.clipboardData.getData('text/plain');
        if (html && html.trim()) {
            setter(cleanExcelHtml(html));
        } else if (plain && plain.trim()) {
            const rows = plain.trim().split(/\r?\n/).map((r) => r.split('\t'));
            const body = rows.map((r) =>
                '<tr>' + r.map((c) =>
                    `<td>${(c || '').replace(/&/g, '&amp;').replace(/</g, '&lt;')}</td>`
                ).join('') + '</tr>'
            ).join('');
            setter(`<table>${body}</table>`);
        }
    };

    const handleSave = async () => {
        if (!htmlEn || !htmlEn.trim()) {
            setError('Please paste worksheet content (English)');
            return;
        }
        setError(null);
        setSaving(true);

        const content_data = { en: { title: title || '', content: htmlEn } };
        if (addHindi && htmlHi && htmlHi.trim()) {
            content_data.hi = { title: title || '', content: htmlHi };
        }

        const { url, method, payload } = buildBlockSave({
            level, entityId, section, editingBlock,
            blockType: 'worksheet',
            contentData: content_data,
            orderIndex: nextOrderIndex,
        });

        try {
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || 'Save failed');
            }
            onSaved();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="ace-form">
            <h4>{editingBlock ? 'Edit' : 'Add'} Worksheet</h4>
            {error && <div className="ace-error">{error}</div>}

            <label>Title (optional)</label>
            <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Revaluation A/c"
            />

            <label>Worksheet (English) *</label>
            <div className="ace-paste-area" tabIndex={0} ref={enRef} onPaste={handlePaste(setHtmlEn)}>
                📋 Click here and paste from Excel (Ctrl+V)
            </div>
            {htmlEn && (
                <>
                    <p className="ace-image-preview-path">Preview:</p>
                    <WorksheetBlock data={{ title, content: htmlEn }} />
                    <button type="button" onClick={() => setHtmlEn('')} className="ace-cancel-btn">Clear</button>
                </>
            )}

            {!addHindi && (
                <button type="button" onClick={() => setAddHindi(true)}>+ Add Hindi Version</button>
            )}
            {addHindi && (
                <>
                    <label>Worksheet (Hindi, optional)</label>
                    <div className="ace-paste-area" tabIndex={0} ref={hiRef} onPaste={handlePaste(setHtmlHi)}>
                        📋 Click here and paste Hindi worksheet from Excel (Ctrl+V)
                    </div>
                    {htmlHi && (
                        <>
                            <p className="ace-image-preview-path">Preview:</p>
                            <WorksheetBlock data={{ title, content: htmlHi }} />
                            <button type="button" onClick={() => setHtmlHi('')} className="ace-cancel-btn">Clear</button>
                        </>
                    )}
                </>
            )}

            <div className="ace-form-actions">
                <button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                <button onClick={onCancel} className="ace-cancel-btn">Cancel</button>
            </div>
        </div>
    );
};

export default WorksheetForm;